import { useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useDemo } from '@/contexts/DemoContext';
import {
  getNotificationPermission,
  getNotificationSettings,
  showNotification,
} from '@/services/notificationService';
import { dispatchReminder } from '@/components/ReminderNotificationCenter';
import API from '@/utils/api';

const CHECK_INTERVAL = 60 * 1000; // Check every minute

/**
 * useReminderNotifications - Hook to check and trigger reminder notifications
 * 
 * Checks for:
 * - Documents expiring soon
 * - Parking about to expire
 * - Upcoming calendar events
 * - Important dates
 * 
 * In DEMO MODE: Notifications are disabled to avoid 401 errors
 */
const useReminderNotifications = () => {
  const { sessionToken, user } = useAuth();
  const { isDemoMode } = useDemo();

  // Helper to show both browser and in-app notification
  const notifyUser = useCallback((title, body, type, itemId, options = {}) => {
    // In-app notification (always shown)
    dispatchReminder({
      id: `${type}-${itemId}-${Date.now()}`,
      type,
      title,
      message: body,
      itemId,
    });

    // Browser notification (if permission granted)
    if (getNotificationPermission() === 'granted') {
      showNotification(title, {
        body,
        tag: `${type}-${itemId}`,
        icon: '/logo192.png',
        ...options,
      });
    }
  }, []);

  const checkDocumentReminders = useCallback(async (settings) => {
    if (!settings.documentExpiring) return;
    
    try {
      const response = await API.get('/documents/expiring', {
        params: { days: Math.ceil(settings.advanceMinutes.documents / 1440) || 30 }
      });
      
      const documents = response.data || [];
      const now = new Date();
      const notifiedKey = 'notified_documents';
      const notified = JSON.parse(localStorage.getItem(notifiedKey) || '[]');
      
      for (const doc of documents) {
        if (!doc.expiration_date || notified.includes(doc.id)) continue;
        
        const expDate = new Date(doc.expiration_date);
        const daysUntil = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
        const advanceDays = Math.ceil(settings.advanceMinutes.documents / 1440);
        
        if (daysUntil <= advanceDays && daysUntil > 0) {
          notifyUser(
            'Document Expiring Soon',
            `${doc.title} expires in ${daysUntil} day${daysUntil !== 1 ? 's' : ''} (${expDate.toLocaleDateString()})`,
            'document',
            doc.id
          );
          
          notified.push(doc.id);
        }
      }
      
      // Clean up old notifications (keep last 100)
      localStorage.setItem(notifiedKey, JSON.stringify(notified.slice(-100)));
    } catch (error) {
      console.error('Error checking document reminders:', error);
    }
  }, []);

  const checkParkingReminders = useCallback(async (settings) => {
    if (!settings.parkingExpiring) return;
    
    try {
      const response = await API.get('/parking/active');
      const parkings = response.data || [];
      const now = new Date();
      const notifiedKey = 'notified_parking';
      const notified = JSON.parse(localStorage.getItem(notifiedKey) || '{}');
      
      for (const parking of parkings) {
        const endTime = parking.paid_until || parking.leave_by;
        if (!endTime) continue;
        
        const endDate = new Date(endTime);
        const minutesUntil = Math.round((endDate - now) / (1000 * 60));
        const advanceMinutes = settings.advanceMinutes.parking || 15;
        
        // Check if within reminder window and not already notified for this window
        const notifyKey = `${parking.id}_${Math.floor(minutesUntil / 5)}`;
        if (minutesUntil <= advanceMinutes && minutesUntil > 0 && !notified[notifyKey]) {
          notifyUser(
            'Parking Expiring Soon',
            `${parking.title || 'Your parking'} expires in ${minutesUntil} minute${minutesUntil !== 1 ? 's' : ''}`,
            'parking',
            parking.id,
            { requireInteraction: true }
          );
          
          notified[notifyKey] = true;
        }
      }
      
      // Clean old entries (older than 24h worth)
      const cleanedNotified = {};
      Object.keys(notified).slice(-200).forEach(k => cleanedNotified[k] = notified[k]);
      localStorage.setItem(notifiedKey, JSON.stringify(cleanedNotified));
    } catch (error) {
      console.error('Error checking parking reminders:', error);
    }
  }, []);

  const checkCalendarReminders = useCallback(async (settings) => {
    if (!settings.calendarEvents) return;
    
    try {
      const now = new Date();
      const endDate = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      
      const response = await API.get('/calendar/events', {
        params: {
          start_date: now.toISOString(),
          end_date: endDate.toISOString()
        }
      });
      
      const events = response.data || [];
      const notifiedKey = 'notified_calendar';
      const notified = JSON.parse(localStorage.getItem(notifiedKey) || '{}');
      
      for (const event of events) {
        if (!event.start_date) continue;
        
        const startDate = new Date(event.start_date);
        const minutesUntil = Math.round((startDate - now) / (1000 * 60));
        const advanceMinutes = settings.advanceMinutes.calendar || 30;
        
        const notifyKey = `${event.id}_${advanceMinutes}`;
        if (minutesUntil <= advanceMinutes && minutesUntil > 0 && !notified[notifyKey]) {
          notifyUser(
            'Upcoming Event',
            `${event.title} starts in ${minutesUntil} minute${minutesUntil !== 1 ? 's' : ''}`,
            'calendar',
            event.id
          );
          
          notified[notifyKey] = true;
        }
      }
      
      localStorage.setItem(notifiedKey, JSON.stringify(Object.fromEntries(Object.entries(notified).slice(-100))));
    } catch (error) {
      console.error('Error checking calendar reminders:', error);
    }
  }, []);

  const checkImportantDates = useCallback(async (settings) => {
    if (!settings.importantDates) return;
    
    try {
      const response = await API.get('/dates/upcoming', {
        params: { days: Math.ceil(settings.advanceMinutes.dates / 1440) || 7 }
      });
      
      const dates = response.data || [];
      const now = new Date();
      const notifiedKey = 'notified_dates';
      const notified = JSON.parse(localStorage.getItem(notifiedKey) || '[]');
      
      for (const date of dates) {
        if (!date.date || notified.includes(date.id)) continue;
        
        const eventDate = new Date(date.date);
        const daysUntil = Math.ceil((eventDate - now) / (1000 * 60 * 60 * 24));
        const advanceDays = Math.ceil(settings.advanceMinutes.dates / 1440);
        
        if (daysUntil <= advanceDays && daysUntil >= 0) {
          const dayText = daysUntil === 0 ? 'today' : daysUntil === 1 ? 'tomorrow' : `in ${daysUntil} days`;
          notifyUser(
            'Important Date Reminder',
            `${date.title} is ${dayText}`,
            'date',
            date.id
          );
          
          notified.push(date.id);
        }
      }
      
      localStorage.setItem(notifiedKey, JSON.stringify(notified.slice(-100)));
    } catch (error) {
      console.error('Error checking important date reminders:', error);
    }
  }, []);

  const checkAllReminders = useCallback(async () => {
    // Skip in demo mode - no backend calls
    if (isDemoMode) return;
    
    if (!sessionToken || !user) return;
    if (getNotificationPermission() !== 'granted') return;
    
    const settings = getNotificationSettings();
    if (!settings.enabled) return;
    
    // Check all reminder types
    await checkDocumentReminders(settings);
    await checkParkingReminders(settings);
    await checkCalendarReminders(settings);
    await checkImportantDates(settings);
  }, [sessionToken, user, isDemoMode, checkDocumentReminders, checkParkingReminders, checkCalendarReminders, checkImportantDates]);

  // Start checking reminders on mount
  useEffect(() => {
    // Skip in demo mode
    if (isDemoMode) return;
    if (!sessionToken) return;
    
    // Initial check after a delay
    const initialTimeout = setTimeout(checkAllReminders, 5000);
    
    // Periodic checks
    const interval = setInterval(checkAllReminders, CHECK_INTERVAL);
    
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [sessionToken, isDemoMode, checkAllReminders]);

  return { checkAllReminders };
};

export default useReminderNotifications;
